import React, { useState } from 'react';
import { membersApi, accountsApi, savingsPlansApi, debtPlansApi, incomesApi } from '../services/api';
import { IRS_IRA_LIMIT, FICA_RATE } from '../utils/constants';
import Labrador from './Labrador';

// First-run guided setup. Shown over the app for a brand-new (empty) household.
// Every step is skippable — the persistent Setup checklist catches anything left.
// Each "Add" persists immediately through the existing APIs, so partial progress
// is never lost even if the user closes early.

const STEPS = ['Welcome', 'Members', 'Income', 'Accounts', 'Plans', 'Done'];
const ACCT_TYPES = ['Budget', 'Loan', 'Tracking', 'Asset'];
const SAVE_TYPES = [
  { value: 'BROKERAGE', label: 'Brokerage' }, { value: 'IRA', label: 'IRA' },
  { value: 'RETIREMENT_401K', label: '401k' }, { value: 'COLLEGE', label: 'College fund' },
  { value: 'OTHER', label: 'Other goal' },
];
const DEBT_TYPES = [
  { value: 'MORTGAGE', label: 'Mortgage' }, { value: 'STUDENT_LOAN', label: 'Student loan' },
  { value: 'OTHER', label: 'Other' },
];

const inp = { width: '100%' };

export default function OnboardingWizard({ month, userId, onDone }) {
  const [step, setStep] = useState(0);
  const [members,  setMembers]  = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [savings,  setSavings]  = useState([]);
  const [debts,    setDebts]    = useState([]);
  const [incomeAmounts, setIncomeAmounts] = useState({}); // memberId -> amount
  const [busy, setBusy] = useState(false);

  const [mDraft, setMDraft] = useState({ name: '', gross: '', retire: '' });
  const [aDraft, setADraft] = useState({ name: '', type: 'Budget', balance: '' });
  const [sDraft, setSDraft] = useState({ name: '', type: 'BROKERAGE', owner: '', balance: '' });
  const [dDraft, setDDraft] = useState({ name: '', type: 'OTHER', principal: '', base: '' });

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));
  const fail = (e) => window.alert(e?.response?.data?.error || 'Could not save — check the values.');

  const addMember = async () => {
    if (!mDraft.name.trim()) return;
    setBusy(true);
    try {
      const { data: m } = await membersApi.add({
        name: mDraft.name.trim(), monthlyGrossIncome: parseFloat(mDraft.gross) || 0,
        retirementDate: mDraft.retire || null, sortOrder: members.length,
      });
      setMembers(p => [...p, m]);
      setIncomeAmounts(p => ({ ...p, [m.id]: parseFloat(mDraft.gross) || 0 }));
      setMDraft({ name: '', gross: '', retire: '' });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const addAccount = async () => {
    if (!aDraft.name.trim()) return;
    setBusy(true);
    try {
      const raw = parseFloat(aDraft.balance) || 0;
      const balance = aDraft.type === 'Loan' ? -Math.abs(raw) : raw; // loans stored negative
      const { data: a } = await accountsApi.add(month.id, {
        name: aDraft.name.trim(), accountType: aDraft.type, balance,
        isCreditCard: false, isInvestedLiquid: false,
      });
      setAccounts(p => [...p, a]);
      setADraft({ name: '', type: 'Budget', balance: '' });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const addSavings = async () => {
    if (!sDraft.name.trim()) return;
    const personal = sDraft.type === 'IRA' || sDraft.type === 'RETIREMENT_401K';
    let owner = sDraft.owner ? Number(sDraft.owner) : null;
    if (personal && owner == null) {
      if (members.length === 0) { window.alert('Add a household member first — IRA/401k need an owner.'); return; }
      owner = members[0].id;
    }
    setBusy(true);
    try {
      const data = {
        name: sDraft.name.trim(), type: sDraft.type, ownerMemberId: owner,
        balance: parseFloat(sDraft.balance) || 0, sortOrder: savings.length,
      };
      if (sDraft.type === 'IRA') data.yearlyMax = IRS_IRA_LIMIT;
      const { data: s } = await savingsPlansApi.add(data);
      setSavings(p => [...p, s]);
      setSDraft({ name: '', type: 'BROKERAGE', owner: '', balance: '' });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const addDebt = async () => {
    if (!dDraft.name.trim()) return;
    setBusy(true);
    try {
      const { data: d } = await debtPlansApi.add({
        name: dDraft.name.trim(), type: dDraft.type, principal: parseFloat(dDraft.principal) || 0,
        baseMonthlyPmt: parseFloat(dDraft.base) || 0, sortOrder: debts.length,
      });
      setDebts(p => [...p, d]);
      setDDraft({ name: '', type: 'OTHER', principal: '', base: '' });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const close = (markDone) => {
    if (markDone) localStorage.setItem(`tb_wizard_done_${userId}`, '1');
    onDone();
  };

  const finish = async () => {
    setBusy(true);
    try {
      if (members.length > 0) {
        await incomesApi.replace(month.id, members.map(m => ({
          memberId: m.id, label: m.name,
          amount: incomeAmounts[m.id] ?? m.monthlyGrossIncome ?? 0,
        })));
      }
    } catch (e) { /* income seeds lazily from members anyway */ }
    finally { setBusy(false); close(true); }
  };

  // ── step bodies ────────────────────────────────────────────────────────────
  const Added = ({ items, render }) => items.length === 0 ? null : (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <div key={it.id ?? i} style={{ fontSize: 13, color: 'var(--text2)', background: 'var(--surface2)',
          border: '1px solid var(--border)', borderRadius: 8, padding: '7px 11px' }}>{render(it)}</div>
      ))}
    </div>
  );

  const stepBody = () => {
    switch (STEPS[step]) {
      case 'Welcome':
        return (
          <div style={{ textAlign: 'center', padding: '8px 4px' }}>
            <Labrador size={72} animated />
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '14px 0 6px' }}>Welcome to Trigger Budget</h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
              Let's set up your household in a few quick steps — members, income, accounts and goals.
              You can skip any step and finish later from the <strong>Setup</strong> tab.
            </p>
          </div>
        );

      case 'Members':
        return (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
              Add everyone who earns income or owns a savings/debt plan. Taxes default to the{' '}
              <strong>{(FICA_RATE * 100).toFixed(2)}% FICA</strong> baseline — fine-tune in Setup.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.1fr auto', gap: 8, alignItems: 'end' }}>
              <label className="form-group"><span>Name</span>
                <input className="field-input" style={inp} value={mDraft.name}
                  onChange={e => setMDraft(d => ({ ...d, name: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addMember()} /></label>
              <label className="form-group"><span>Gross / mo</span>
                <input className="field-input" type="number" style={inp} value={mDraft.gross}
                  onChange={e => setMDraft(d => ({ ...d, gross: e.target.value }))} /></label>
              <label className="form-group"><span>Retirement date</span>
                <input className="field-input" type="date" style={inp} value={mDraft.retire}
                  onChange={e => setMDraft(d => ({ ...d, retire: e.target.value }))} /></label>
              <button className="btn btn-primary" disabled={busy || !mDraft.name.trim()} onClick={addMember}>Add</button>
            </div>
            <Added items={members} render={m => `${m.name} · ${m.monthlyGrossIncome ? '$' + m.monthlyGrossIncome + '/mo' : 'no income'}`} />
          </div>
        );

      case 'Income':
        return (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
              Confirm each member's monthly income (you can adjust a single month later in Strategy).
            </p>
            {members.length === 0
              ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No members yet — go back to add some, or skip.</div>
              : members.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 14 }}>{m.name}</span>
                  <input className="field-input" type="number" style={{ width: 140, textAlign: 'right' }}
                    value={incomeAmounts[m.id] ?? ''}
                    onChange={e => setIncomeAmounts(p => ({ ...p, [m.id]: parseFloat(e.target.value) || 0 }))} />
                </div>
              ))}
          </div>
        );

      case 'Accounts':
        return (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
              Add your accounts and current balances. <strong>Loan</strong> balances are stored as
              what you owe (entered as a positive number).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <label className="form-group"><span>Name</span>
                <input className="field-input" style={inp} value={aDraft.name}
                  onChange={e => setADraft(d => ({ ...d, name: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addAccount()} /></label>
              <label className="form-group"><span>Type</span>
                <select className="field-input" style={inp} value={aDraft.type}
                  onChange={e => setADraft(d => ({ ...d, type: e.target.value }))}>
                  {ACCT_TYPES.map(t => <option key={t}>{t}</option>)}</select></label>
              <label className="form-group"><span>Balance</span>
                <input className="field-input" type="number" style={inp} value={aDraft.balance}
                  onChange={e => setADraft(d => ({ ...d, balance: e.target.value }))} /></label>
              <button className="btn btn-primary" disabled={busy || !aDraft.name.trim()} onClick={addAccount}>Add</button>
            </div>
            <Added items={accounts} render={a => `${a.name} · ${a.accountType} · $${a.balance}`} />
          </div>
        );

      case 'Plans':
        return (
          <div style={{ display: 'grid', gap: 18 }}>
            <div>
              <div className="sec-label" style={{ marginBottom: 8 }}>Savings &amp; investments</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <label className="form-group"><span>Name</span>
                  <input className="field-input" style={inp} value={sDraft.name}
                    onChange={e => setSDraft(d => ({ ...d, name: e.target.value }))} /></label>
                <label className="form-group"><span>Type</span>
                  <select className="field-input" style={inp} value={sDraft.type}
                    onChange={e => setSDraft(d => ({ ...d, type: e.target.value }))}>
                    {SAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></label>
                <label className="form-group"><span>Owner</span>
                  <select className="field-input" style={inp} value={sDraft.owner}
                    onChange={e => setSDraft(d => ({ ...d, owner: e.target.value }))}>
                    <option value="">shared</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
                <label className="form-group"><span>Balance</span>
                  <input className="field-input" type="number" style={inp} value={sDraft.balance}
                    onChange={e => setSDraft(d => ({ ...d, balance: e.target.value }))} /></label>
                <button className="btn btn-primary" disabled={busy || !sDraft.name.trim()} onClick={addSavings}>Add</button>
              </div>
              <Added items={savings} render={s => `${s.name} · ${s.type}`} />
            </div>
            <div>
              <div className="sec-label" style={{ marginBottom: 8 }}>Debts</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <label className="form-group"><span>Name</span>
                  <input className="field-input" style={inp} value={dDraft.name}
                    onChange={e => setDDraft(d => ({ ...d, name: e.target.value }))} /></label>
                <label className="form-group"><span>Type</span>
                  <select className="field-input" style={inp} value={dDraft.type}
                    onChange={e => setDDraft(d => ({ ...d, type: e.target.value }))}>
                    {DEBT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></label>
                <label className="form-group"><span>Principal</span>
                  <input className="field-input" type="number" style={inp} value={dDraft.principal}
                    onChange={e => setDDraft(d => ({ ...d, principal: e.target.value }))} /></label>
                <label className="form-group"><span>Base pmt</span>
                  <input className="field-input" type="number" style={inp} value={dDraft.base}
                    onChange={e => setDDraft(d => ({ ...d, base: e.target.value }))} /></label>
                <button className="btn btn-primary" disabled={busy || !dDraft.name.trim()} onClick={addDebt}>Add</button>
              </div>
              <Added items={debts} render={d => `${d.name} · ${d.type}`} />
            </div>
          </div>
        );

      case 'Done':
      default:
        return (
          <div style={{ textAlign: 'center', padding: '8px 4px' }}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: '8px 0 6px' }}>You're all set</h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', maxWidth: 420, margin: '0 auto 12px', lineHeight: 1.6 }}>
              Added {members.length} member{members.length !== 1 ? 's' : ''}, {accounts.length} account
              {accounts.length !== 1 ? 's' : ''}, {savings.length + debts.length} plan
              {savings.length + debts.length !== 1 ? 's' : ''}. Next, review your plan in <strong>Strategy</strong>{' '}
              and hit <strong>Save all</strong> — then assign in <strong>Budget</strong>.
            </p>
          </div>
        );
    }
  };

  const isLast = step === STEPS.length - 1;

  return (
    <div className="modal-overlay" style={{ alignItems: 'flex-start', paddingTop: '6vh', overflowY: 'auto' }}>
      <div className="modal-box" style={{ width: 680, maxWidth: '94vw' }}>
        <div style={{ padding: '18px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {STEPS.map((_, i) => (
              <span key={i} style={{ width: i === step ? 22 : 8, height: 8, borderRadius: 99,
                background: i <= step ? 'var(--blue)' : 'var(--border2)', transition: 'all 0.2s' }} />
            ))}
          </div>
          <button className="btn btn-sm" onClick={() => close(true)}>Skip setup</button>
        </div>

        <div style={{ padding: '18px 24px 6px', minHeight: 220 }}>{stepBody()}</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 24px 22px' }}>
          <button className="btn" onClick={back} disabled={step === 0}>Back</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isLast && step > 0 && <button className="btn" onClick={next} disabled={busy}>Skip</button>}
            {isLast
              ? <button className="btn btn-primary" onClick={finish} disabled={busy}>Go to my budget</button>
              : <button className="btn btn-primary" onClick={next} disabled={busy}>
                  {step === 0 ? 'Get started' : 'Next'}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
}
